/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState } from 'react';
import {
    object, string, array, shape, bool,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import { withStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import GraphqlWineDisclaimer from '../../GraphqlWineDisclaimer/WineDisclaimerContainer';
import { selectedSkuType } from '../../common/types';
import FoodDetailsExpansionPanel from './FoodDetailsExpansionPanel';
import MarketingSpot from '../Partials/MarketingSpot';
import useIsPlaPage from '../../../../../../helpers/hooks/useIsPlaPage';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../../helpers/hooks/useIsDisplayChannel';
import replaceFontWeight from '../../../../../../helpers/replaceFontWeight';
import replaceSpecialChar from '../../../../../../helpers/replaceSpecialChar';

const styles = (theme) => ({
    root: {
        marginBottom: 20,
        width: '100%',
        overflow: 'hidden',
        border: 'none',
        marginTop: 30,
        // Mobile
        [theme.breakpoints.down(1025)]: {
            display: 'none',
        },
    },
    tabContentsList: {
        width: '31%',
        borderRight: `1px solid ${theme?.palette?.primary[200]}`,
        float: 'left',
        paddingLeft: '2%',
    },
    shortDescription: {
        padding: '0% 0% 0% 5%',
    },
    contents: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        paddingRight: 0,
        fontWeight: '600',
        fontSize: 20,
    },
    productDetails: {
        background: theme?.palette?.globalBannerMainTextColor,
        color: theme?.palette?.common?.white,
        padding: '10px',
        fontWeight: '600',
        margin: 0,
        fontSize: 22,
    },
    tabProductDetails: {
        width: '100%',
        display: 'grid',
        paddingBottom: '2%',
        [theme.breakpoints.up('md')]: {
            display: 'block',
        },
    },
    shortDescriptionCopy: {
        width: '96%',
        paddingBottom: '10px',
    },
    additionalInformation: {
        '& img': {
            marginRight: 20,
            alignSelf: 'center',
            height: 'fit-content',
        },
        '& p': {
            display: 'flex',
            fontSize: '0.8em',
        },
        margin: '10px 0',
        padding: 10,
    },
    additionalInformationContainer: {
        width: '96%',
        borderTop: `1px solid ${theme.palette.grey8 || '#ccc'}`,
    },
    contentDesc: {
        overflowX: 'hidden',
        overflowY: 'auto',
    },
    plaRoot: {
        marginTop: 0,
    },
    heading: {
        textAlign: 'left',
        fontSize: '32px',
        lineHeight: '37px',
        maxWidth: '1000px',
        margin: '0 auto',
        paddingBottom: '32px',
        borderBottom: '1px solid #d8d8d8',
    },
    rootWrapper: {
        background: '#fafafa',
        padding: '60px 0px',
    },
});

const GraphqlFoodDetailsUIDesktop = ({
    classes, productContentsDescription, longDescription, shortDescription,
    selectedSku, additionalInformation, isPassportBundleProduct, productAttributes,
    isSinglePlaThree, renderContentOnly, isPdpRedesignEnabled, productName, partNumber,

}) => {
    const seed = useUIDSeed();
    const descriptionDiv = useRef(null);
    const contentsClassName = classes.contents;
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaPage = useIsPlaPage();
    const [open, close] = useState(true);
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const plaThreeColumn = (isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column';
    /**
     * @productContentsDescription it is the long description from selected/default sku
     * @longDescription is the description of product
     */
    const renderProductContent = () => {
        let content = null;
        if (productContentsDescription?.trim()?.length > 0) {
            content =  ReactHtmlParser(productContentsDescription, {
                transform(node) {
                    if (node?.type === 'tag' && node?.name === 'a') {
                        const nodesData = node;
                        const newData = nodesData?.children?.some((getNode) => getNode?.children?.some((getNode1) => getNode1?.data === 'click here'));
                        const nodeDataFind =  nodesData?.children?.some((getNode) => getNode?.data === 'click here');
                        if (nodesData && (newData || nodeDataFind)) {
                            nodesData.attribs['aria-label'] = 'click-here';
                        }
                    }
                    replaceFontWeight(node);
                }, // Added aria-label on click here link for accessibility

            });
        } else if (longDescription?.trim()?.length > 0) {
            content = ReactHtmlParser(longDescription, {
                transform(node) {
                    replaceFontWeight(node);
                },
            });
        }
        return content;
    };

    const renderAdditionalInformation = () => (
        additionalInformation.length ? (
            <div className={classes.additionalInformationContainer}>
                {
                    additionalInformation.map((info) => {
                        switch (Object.keys(info)?.[0]) {
                            case 'image':
                                return null;
                            case 'copy':
                                return (
                                    <div
                                        key={seed(info)}
                                        style={{ backgroundColor: info.copy?.background_color?.color }}
                                        className={classes.additionalInformation}
                                    >
                                        <ReactMarkdown source={info.copy?.copy} />
                                    </div>
                                );
                            default:
                                return null;
                        }
                    })
                }
            </div>
        ) : null
    );

    const renderProductDescription = (withAdditionalInfo) => (
        <>
            {ReactHtmlParser(shortDescription, {
                transform(node) {
                    replaceFontWeight(node);
                },
            })}
            {selectedSku?.ageVerifyFlag === 'true'
                && (
                    <div>
                        <div className={classes.divider} />
                        <GraphqlWineDisclaimer />
                    </div>
                )}
            {selectedSku?.subscriptions?.type === 'Discovery' && (
                <div>
                    <MarketingSpot title="Discovery Subscription Disclaimer" brandId={selectedSku?.brandId} />
                </div>
            )}
            {withAdditionalInfo && renderAdditionalInformation()}
        </>
    );

    return (
        <div className={`${plaThreeColumn ? classes.plaRoot : ''} ${classes.root} ${isPdpRedesignEnabled ? classes.rootWrapper : ''}`}ref={descriptionDiv}>
            {!isSinglePlaThree && (
                <>
                    {isPdpRedesignEnabled ? (
                        <div className={classes.heading}>
                            <strong>
                                About {ReactHtmlParser(replaceSpecialChar(productName))}
                            </strong>
                        </div>
                    ) : ''}
                    <FoodDetailsExpansionPanel summary="Contents" details={renderProductContent()} panelId={1} expanded selectedSku={selectedSku} productAttributes={productAttributes} isPdpRedesignEnabled={isPdpRedesignEnabled} partNumber={partNumber} />
                    <FoodDetailsExpansionPanel summary="Description" details={renderProductDescription(true)} panelId={2} expanded={isPdpRedesignEnabled ? false : !plaThreeColumn} isPdpRedesignEnabled={isPdpRedesignEnabled} />
                </>
            )}
            {isSinglePlaThree && (
                <>
                    {
                        renderContentOnly
                            ? (
                                <FoodDetailsExpansionPanel summary="Contents" details={renderProductContent()} panelId={1} expanded selectedSku={selectedSku} productAttributes={productAttributes} />
                            )
                            : (
                                <></>
                            )
                    }
                </>
            )}
            {
                isPassportBundleProduct?.enable
                && (
                    <div className={classes.shortDescription}>
                        <strong className={contentsClassName}>Celebrations Passport Details: </strong>{isPassportBundleProduct?.details?.item_sku}
                        <div className={classes.shortDescriptionCopy}>
                            {ReactHtmlParser(isPassportBundleProduct?.details?.bundle_details?.long_description, {
                                transform(node) {
                                    replaceFontWeight(node);
                                },
                            })}
                        </div>
                    </div>
                )
            }
        </div>
    );
};

GraphqlFoodDetailsUIDesktop.propTypes = {
    classes: object.isRequired,
    shortDescription: string.isRequired,
    longDescription: string.isRequired,
    selectedSku: selectedSkuType,
    productContentsDescription: string,
    additionalInformation: array,
    isPassportBundleProduct: shape({
        enable: bool,
        details: shape({
            bundle_details: shape({
                long_description: string,
            }),
        }),
    }),
    productAttributes: array,
    isSinglePlaThree: bool,
    renderContentOnly: bool,
    isPdpRedesignEnabled: bool,
    productName: string,
    partNumber: string,
};

GraphqlFoodDetailsUIDesktop.defaultProps = {
    selectedSku: {},
    productContentsDescription: '',
    additionalInformation: [],
    isPassportBundleProduct: {},
    productAttributes: [],
    isSinglePlaThree: false,
    renderContentOnly: false,
    isPdpRedesignEnabled: false,
    productName: '',
    partNumber: '',
};

export default withStyles(styles)(GraphqlFoodDetailsUIDesktop);
